function createBatchActions({ apiFetch, state, toast, syncFromApi }) {
  const approveBatch = async (id) => {
    const batch = state.batches.find((b) => b.id === id);
    if (!batch?.approvalId) {
      toast('error', 'Aprovação indisponível', 'Este lote não possui registro de aprovação na API.');
      return;
    }
    try {
      await apiFetch(`/api/approvals/${batch.approvalId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: 'approved',
          reviewed_by: state.currentUser?.id || null,
          reviewed_at: new Date().toISOString(),
        }),
      });
      await syncFromApi(undefined, 'batches');
      toast('success', 'Lote aprovado', `${id} foi aprovado com sucesso.`);
    } catch (error) {
      toast('error', 'Falha ao aprovar', error.message);
    }
  };

  const rejectBatch = async (id, notes) => {
    const batch = state.batches.find((b) => b.id === id);
    if (!batch?.approvalId) {
      toast('error', 'Aprovação indisponível', 'Este lote não possui registro de aprovação na API.');
      return;
    }
    try {
      await apiFetch(`/api/approvals/${batch.approvalId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: 'rejected',
          reviewed_by: state.currentUser?.id || null,
          reviewed_at: new Date().toISOString(),
          ...(notes ? { notes } : {}),
        }),
      });
      await syncFromApi(undefined, 'batches');
      toast('info', 'Lote rejeitado', `${id} foi rejeitado.`);
    } catch (error) {
      toast('error', 'Falha ao rejeitar', error.message);
    }
  };

  const createBatchDraft = async (companyId, payload) => {
    const cid = Number(companyId || 0);
    if (!cid) {
      throw new Error('Empresa não selecionada para salvar o lote.');
    }
    const data = await apiFetch(`/api/companies/${cid}/lots`, {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
    await syncFromApi(undefined, 'batches');
    return data;
  };

  const deleteBatch = async (batchId) => {
    const batch = state.batches.find((b) => b.id === batchId);
    if (!batch?.lotId) {
      toast('error', 'Excluir lote', 'Este lote não possui identificador na API.');
      return false;
    }
    if (batch.status === 'transmitted') {
      toast('info', 'Excluir lote', 'Pagamentos já transmitidos ao Sienge não podem ser excluídos.');
      return false;
    }
    try {
      await apiFetch(`/api/lots/${batch.lotId}`, { method: 'DELETE' });
      await syncFromApi(undefined, 'batches');
      toast('success', 'Lote excluído', `${batchId} foi removido.`);
      return true;
    } catch (error) {
      if (error?.status === 422 && error?.body?.code === 'lot_transmitted') {
        toast('info', 'Excluir lote', error.body.message || 'Este pagamento já foi transmitido ao Sienge e não pode ser excluído.');
        return false;
      }
      toast('error', 'Excluir lote', error?.message || 'Falha ao excluir o lote.');
      return false;
    }
  };

  const fetchLotDetail = async (lotId) => {
    const id = Number(lotId || 0);
    if (!id) return null;
    return apiFetch(`/api/lots/${id}`);
  };

  const verifyLotPendencies = async (lotId) => {
    const id = Number(lotId || 0);
    if (!id) throw new Error('Lote inválido para verificação.');
    const data = await apiFetch(`/api/lots/${id}/verify-pendencies`, {
      method: 'POST',
    });
    await syncFromApi(undefined, 'batches');
    return data;
  };

  const updateLotPaymentData = async (lotId, payload) => {
    const id = Number(lotId || 0);
    if (!id) throw new Error('Lote inválido para atualização.');
    const data = await apiFetch(`/api/lots/${id}/payment-data`, {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
    await syncFromApi(undefined, 'batches');
    return data;
  };

  const fetchArchivedBatches = async (companyId) => {
    const [lotsData, approvalsData] = await Promise.all([
      apiFetch(`/api/lots?archived=1${companyId ? `&company_id=${companyId}` : ''}`),
      apiFetch('/api/approvals'),
    ]);
    const { mapLotsToBatches } = await import('../sync-mappers.js');
    return mapLotsToBatches(lotsData?.data || [], approvalsData?.data || []);
  };

  const resubmitApproval = async (approvalId) => {
    await apiFetch(`/api/approvals/${approvalId}`, {
      method: 'PATCH',
      body: JSON.stringify({ status: 'pending' }),
    });
    await syncFromApi(undefined, 'batches');
  };

  const fetchPaymentSyncLogs = async (query = {}) => {
    const qs = new URLSearchParams(query).toString();
    return apiFetch(`/api/payments/sync-logs${qs ? `?${qs}` : ''}`);
  };

  const fetchPaymentQueueStats = async (companyId) => {
    const qs = companyId ? `?company_id=${companyId}` : '';
    return apiFetch(`/api/payments/sync-logs/queue-stats${qs}`);
  };

  const togglePaymentPause = async (paymentId, action) => {
    return apiFetch(`/api/payments/${paymentId}/toggle-pause`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    });
  };

  return {
    approveBatch,
    rejectBatch,
    createBatchDraft,
    deleteBatch,
    fetchLotDetail,
    verifyLotPendencies,
    updateLotPaymentData,
    resubmitApproval,
    fetchArchivedBatches,
    fetchPaymentSyncLogs,
    fetchPaymentQueueStats,
    togglePaymentPause,
  };
}

export { createBatchActions };
